import { Box, Typography } from '@mui/material';
import type { Edge } from 'reactflow';
import type { User } from '../../../util/Types/User';
import TagInput from '../../Test/TagInput';
import ConnectionStatus from '../../ThirdPartyIntegration/common/ConnectionStatus';
import type { IntegrationProviderConfig } from '../../ThirdPartyIntegration/types';
import { isActiveEdge } from '../../../util/findActiveHandle';
import type { McpNodeFormState } from '../../../types/mcpNode';
import {
  MCP_TAG_FIELD_HEIGHT,
  MCP_TAG_INPUT_WIDTH,
} from './mcpNodeUiConstants';


export interface McpConnectionPanelProps {
  user: User | null;
  organizationId: string;
  nodeId: string;
  edges: Edge[];
  data: Record<string, unknown>;
  formData: McpNodeFormState;
  mcpProviderConfig: IntegrationProviderConfig | undefined;
  showServerUrlField: boolean;
  showAuthTokenField: boolean;
  savedHttpIntegrationSelected: boolean;
  onChange: (e: { target: { name: string; value: string } }) => void;
  onIntegrationSelected: (integrationId: string) => void;
  onIntegrationDisconnected: () => void;
  findColor: { lineColor?: string; handleColor?: string } | undefined;
}

const McpConnectionPanel = ({
  user,
  organizationId,
  nodeId,
  edges,
  data,
  formData,
  mcpProviderConfig,
  showServerUrlField,
  showAuthTokenField,
  savedHttpIntegrationSelected,
  onChange,
  onIntegrationSelected,
  onIntegrationDisconnected,
  findColor,
}: McpConnectionPanelProps) => {
  const serverUrlTargetActive = isActiveEdge(edges, nodeId, 'serverUrl', 'target');
  const authTokenTargetActive = isActiveEdge(edges, nodeId, 'authToken', 'target');

  return (
    <Box>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ display: 'block', mb: 1.5 }}
      >
        Connection
      </Typography>
      {organizationId ? (
        <Box className="nodrag">
          <ConnectionStatus
            provider="mcp"
            config={mcpProviderConfig}
            selectedIntegrationId={formData.integrationId || undefined}
            currentUserId={user?.id}
            onConnectionSelected={(integrationId, _name) => {
              onIntegrationSelected(integrationId);
            }}
            onDisconnected={onIntegrationDisconnected}
          />
        </Box>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1.5 }}
        >
          Select a workspace to add or pick a saved MCP (HTTP) connection.
          Workspace members can use connections created by teammates; secrets
          stay on the server.
        </Typography>
      )}
      {showServerUrlField ? (
        <Box sx={{ mt: 1.5 }}>
          <TagInput
            name="serverUrl"
            placeholder="https://mcp.example.com"
            labelName="MCP Server URL"
            handleId="serverUrl"
            width={MCP_TAG_INPUT_WIDTH}
            height={MCP_TAG_FIELD_HEIGHT}
            isCollapsed={true}
            nodeId={nodeId}
            isActive={serverUrlTargetActive}
            isAdvanced={true}
            findColor={findColor}
            removeIcons
            advancedCollapsed={true}
            onChange={onChange}
            dataConnected={(data?.serverUrlData as { text: string }[]) || []}
            placement={data?.placement as string | undefined}
            value={formData.serverUrl}
            nodeLabel={data?.label as string | undefined}
          />
        </Box>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1.5 }}
        >
          {savedHttpIntegrationSelected
            ? 'MCP server URL comes from the selected MCP (HTTP) connection. Clear that connection to use the card, or wire the MCP Server URL handle to override.'
            : 'Use the MCP (HTTP) card above for server URL and optional bearer token, or wire the MCP Server URL handle.'}
        </Typography>
      )}
      {showAuthTokenField ? (
        <Box sx={{ mt: 1.5 }}>
          <TagInput
            name="authToken"
            placeholder="OAuth / Bearer token (optional)"
            labelName="Auth token"
            handleId="authToken"
            width={MCP_TAG_INPUT_WIDTH}
            height={MCP_TAG_FIELD_HEIGHT}
            isCollapsed={true}
            nodeId={nodeId}
            isActive={authTokenTargetActive}
            isAdvanced={true}
            findColor={findColor}
            removeIcons
            advancedCollapsed={true}
            onChange={onChange}
            dataConnected={(data?.authTokenData as { text: string }[]) || []}
            placement={data?.placement as string | undefined}
            value={formData.authToken}
            nodeLabel={data?.label as string | undefined}
          />
        </Box>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1.5 }}
        >
          Bearer token is set in the MCP (HTTP) card above, via OAuth, or by
          wiring the auth token handle.
        </Typography>
      )}
    </Box>
  );
};

export default McpConnectionPanel;
